import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FiTrash2, FiX, FiAlertTriangle } from 'react-icons/fi';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.15s ease;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.body === '#fafbfc' ? '#fff' : '#1c1c1e'};
  border: 1px solid ${({ theme }) => theme.border2};
  border-radius: 16px;
  padding: 2em;
  width: 360px;
  max-width: calc(100vw - 2em);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  animation: ${slideUp} 0.2s ease;
  position: relative;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1em;
  right: 1em;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  opacity: 0.4;
  font-size: 1.1em;
  display: flex;
  align-items: center;
  padding: 0.2em;
  border-radius: 4px;
  transition: opacity 0.2s;
  &:hover { opacity: 0.8; }
`;

const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.2em;

  svg {
    font-size: 1.4em;
    color: #ef4444;
  }
`;

const Title = styled.h3`
  font-size: 1em;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin: 0 0 0.4em;
`;

const Description = styled.p`
  font-size: 0.85em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0 0 1.6em;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.6em;
`;

const Btn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4em;
  padding: 0.75em;
  font-size: 0.85em;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  background: ${({ $danger, theme }) =>
    $danger ? '#ef4444' : (theme.body === '#fafbfc' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.07)')};
  color: ${({ $danger, theme }) => $danger ? '#fff' : theme.text};
  border: 1px solid ${({ $danger, theme }) =>
    $danger ? '#ef4444' : (theme.body === '#fafbfc' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')};

  svg { font-size: 0.95em; }

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);
  }

  &:active { transform: translateY(0); }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function ConfirmDeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirmar exclusão',
  description = 'Esta ação não pode ser desfeita.',
  confirmLabel = 'Deletar',
  loading = false,
}) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={onClose}><FiX /></CloseBtn>

        <IconWrap>
          <FiAlertTriangle />
        </IconWrap>

        <Title>{title}</Title>
        <Description>{description}</Description>

        <ButtonRow>
          <Btn onClick={onClose} disabled={loading}>
            Cancelar
          </Btn>
          <Btn $danger onClick={onConfirm} disabled={loading}>
            <FiTrash2 />
            {loading ? 'Deletando...' : confirmLabel}
          </Btn>
        </ButtonRow>
      </Box>
    </Overlay>
  );
}
